import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { TCurrentTime } from "libs/design-system/src/types/time_picker";
import { TimePicker, ToggleBox } from '@unified-checkout/design-system';

import DateRanges from "@app/dashboard/components/inner_layout/DateRanges";
import { useLangStore } from "@app/dashboard/store/lang";
import { customLocales } from '@app/dashboard/utils/dateRangePicker';
import type { TAppliedRanges } from "@app/dashboard/types/innerLayout/date-ranges";
import type {
  IRestrictionsSection
} from "@app/dashboard/types/innerLayout/payment_links/bulk/sections/restrictions";
import {
  paymentLinksDatePresets,
  DEFAULT_TIME,
  formatDateAndTime,
  BULK_PLS_SECTIONS_DIMENSIONS
} from "../../../utils";
import { SectionLoader } from "../../../Loader";
import styles from "./restrictions.module.scss";


export const Restrictions = ({
  fullPageStatus,
  setFormValues
}: IRestrictionsSection) => {
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const [hasExpDate, setHasExpDate] = useState(false);

  const preDefinedRanges = useMemo(() => paymentLinksDatePresets(t), [t]);
  const defaultPreDefinedRange = useMemo(
    () => preDefinedRanges.find((range) => range.default),
    [preDefinedRanges]
  );

  const [time, setTime] = useState<TCurrentTime | undefined>(DEFAULT_TIME);
  const [appliedRanges, setAppliedRanges] = useState<TAppliedRanges>(/* {
    index: defaultPreDefinedRange?.index,
    name: defaultPreDefinedRange?.name,
    start: defaultPreDefinedRange?.selectionRange[0],
    end: defaultPreDefinedRange?.selectionRange[1]
  } */);

  useEffect(() => {
    if (appliedRanges && time) {
      const formattedDate = formatDateAndTime(appliedRanges?.start as any, time as any);
      setFormValues(prev => ({ ...prev, expiresAt: formattedDate as string }));
    }
  }, [time, appliedRanges]);


  const onApplyHandler = (range?: TAppliedRanges) => {
    setAppliedRanges(() => range);
  };

  const onClearDateHandler = () => {
    const index = defaultPreDefinedRange?.index;
    const name = defaultPreDefinedRange?.name;
    const startDate = defaultPreDefinedRange?.selectionRange[0];
    const endDate = defaultPreDefinedRange?.selectionRange[1];

    setAppliedRanges({
      index,
      name,
      start: startDate,
      end: endDate
    });
  };

  return (
    fullPageStatus.loading ?
      <SectionLoader {...BULK_PLS_SECTIONS_DIMENSIONS.RESTRICTIONS} />
      :
      <div className={styles["restrictions"]}>

        <div className={styles["restrictions__title"]}>
          {t('innerLayout.paymentLinksCreationPages.labels.restrictions')}
        </div>

        <div className={styles["restrictions__actions-box"]}>

          <div className={styles["restrictions__actions-box__exp-toggle"]}>

            <ToggleBox
              size="sm"
              type="transparent box"
              text={/* hasExpDate ?  */t('innerLayout.paymentLinksCreationPages.sections.restrictions.hasExpDate')/*  : t('innerLayout.paymentLinksCreationPages.sections.restrictions.noExpDate') */}
              // hintText={hasExpDate ? '' : t('innerLayout.paymentLinksCreationPages.sections.restrictions.activeUntilDisable')}
              checked={hasExpDate}
              onToggleChangeHandler={(checked) => {
                setHasExpDate(checked);
                setFormValues(prev => ({ ...prev, customExpDate: checked }));
              }}
            />

            {/* {
              !hasExpDate &&
              <div className={styles["restrictions__actions-box__exp-toggle__hint"]}>
                {t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.sections.restrictions.willExpireInOneHour')}
              </div>
            } */}

          </div>

          {
            hasExpDate &&
            <div className={styles["restrictions__actions-box__date-and-time"]}>
              <DateRanges
                appliedRanges={appliedRanges as TAppliedRanges}
                handleRangesChange={onApplyHandler}
                lang={lang}
                minDate={defaultPreDefinedRange?.selectionRange[0]}
                maxDate={defaultPreDefinedRange?.selectionRange[1]}
                onClear={onClearDateHandler}
                customLocale={customLocales}
                customPreDefinedRanges={preDefinedRanges}
                shouldSelectOneDate
                verticalPlacement="auto"
                horizontalPlacement="start"
                placeholder={t(
                  'innerLayout.paymentLinksCreationPages.sections.restrictions.datePicker.placeholder'
                )}
                shouldApplyDefaultRange={false}
                shouldHideBtnRangeName={true}
              />
              <TimePicker
                name='bulk-payment-exp-time'
                defaultValue={time}
                disabled={!appliedRanges}
                onChangeHandler={setTime}
              />

            </div>
          }

        </div>

      </div>
  )
}
